import { FC } from 'react';
import { Stack } from '@mui/material';
import {
  Chip,
  Modal,
  SelectInput,
  TextField,
  Typography,
} from 'melp-design/components';
import { Controller, useForm } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';

export type StrictnessKey = 'low' | 'medium' | 'high';
export const STRICTNESS_BY_KEY: Record<StrictnessKey, number> = {
  low: 0,
  medium: 0.5,
  high: 1,
};

interface Values {
  name: string;
  description: string;
  enabled: boolean;
  strictness: number;
}

const INITIAL_VALUES: Values = {
  name: '',
  description: '',
  enabled: true,
  strictness: STRICTNESS_BY_KEY['medium'],
};

interface Props extends ModalProps {
  initialValues?: Values;
  closeModal: (
    param?: { action: 'CLOSE' } | { action: 'CONFIRM'; values: Values },
  ) => void;
}

const SAMPLES: Array<{ name: string; description: string }> = [
  {
    name: 'Meaningful Message',
    description:
      'The message is meaningful (not just repeated phrases or generic filler).',
  },
  {
    name: 'Clear and Grammatical',
    description: 'The message is clear and grammatically understandable.',
  },
  {
    name: 'Achievement Relevance',
    description: 'The message is relevant to the selected achievement.',
  },
  {
    name: 'No self promotion',
    description: 'No self promotion, no bragging, no boasting.',
  },
  {
    name: 'Positivity / Constructive Tone',
    description: 'No demeaning, sarcasm, passive aggression.',
  },
  {
    name: 'No Discrimination or Harassing',
    description:
      'No discrimnation based on race, gender, religion, sexual orientation, disability, age, marital status.',
  },
  {
    name: 'No Confidential Information',
    description: 'No project details, financial figures, no client names.',
  },
  {
    name: 'Personal Data Protection',
    description:
      'No personal addresses, phone numbers, personal IDs or health information.',
  },
  {
    name: 'No Political or Religious Endorsements',
    description:
      'No explicit political parties, candidates, religious statements. Inclusive well-wishing (e.g. Happy Christmas) is still allowed.',
  },
  {
    name: 'Anonimity',
    description: 'No names, no personal information references.',
  },
  {
    name: 'Achievement Relevance',
    description:
      'The recognition message must be relevant to the achievement being recognized. Messages that do not relate to the specific achievement should be flagged.',
  },
];

export const AiRuleModal: FC<Props> = ({
  closeModal,
  initialValues = INITIAL_VALUES,
}) => {
  const { t } = useTranslation();

  const { control, handleSubmit, setValue } = useForm<Values>({
    defaultValues: initialValues,
  });

  return (
    <form
      onSubmit={handleSubmit((values) => {
        closeModal({ action: 'CONFIRM', values });
      })}
    >
      <Modal.Content
        title={t('automation.addRule')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="name"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('common.name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('employees.details')}
                name={name}
                value={value}
                onChange={onChange}
                rows={3}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="strictness"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <SelectInput
                  label={t('recognitions.rules.strictness.label')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  options={Object.entries(STRICTNESS_BY_KEY).map(
                    ([key, value]) => ({
                      label: t(
                        `recognitions.suggestionRules.strictness.${
                          key as StrictnessKey
                        }`,
                      ),
                      value,
                    }),
                  )}
                  required
                  ref={ref}
                />
              );
            }}
          />

          <Stack gap={1}>
            <Typography color="textSecondary">
              {t('recognitions.suggestionRules.sample.label')}
            </Typography>

            <Stack flexDirection="row" flexWrap="wrap" gap={1}>
              {SAMPLES.map((sample) => (
                <Chip
                  key={sample.name}
                  label={sample.name}
                  variant="neutral"
                  onClick={() => {
                    setValue('name', sample.name);
                    setValue('description', sample.description);
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Modal.Content>
    </form>
  );
};
